
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Organization } from "@/types/organization";

export const useOrganization = () => {
  const subdomain = useMemo(() => {
    if (typeof window === 'undefined') return 'swift';
    
    const hostname = window.location.hostname;
    
    // Handle localhost development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'swift';
    }
    
    // Extract subdomain from hostname
    const parts = hostname.split('.');
    if (parts.length > 2) {
      return parts[0]; // First part is subdomain
    }
    
    // Fallback to swift
    return 'swift';
  }, []);

  // Memoize query options to prevent unnecessary re-renders
  const queryOptions = useMemo(() => ({
    queryKey: ['organization', subdomain],
    queryFn: async (): Promise<Organization> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('subdomain', subdomain)
        .eq('is_active', true)
        .single();

      if (error) {
        // If organization not found, fallback to Swift
        if (error.code === 'PGRST116') {
          const { data: swiftOrg, error: swiftError } = await supabase
            .from('organizations')
            .select('*')
            .eq('subdomain', 'swift')
            .single();
          
          if (swiftError) throw swiftError;
          return swiftOrg;
        }
        throw error;
      }

      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  }), [subdomain]);

  const { data: organization, isLoading, error } = useQuery(queryOptions);

  return {
    organization,
    isLoading,
    error,
    subdomain,
  };
};
